import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import api, { fileUrl, apiErr } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Package, TrendingUp, DollarSign, Eye, Plus, Pencil, Trash2, Store, Loader2, Ticket, Clock } from "lucide-react";

const Metric = ({ icon: Icon, label, value }) => (
  <div className="bg-white border border-[#E2E8F0] p-6">
    <div className="flex items-center gap-2 text-[#94A3B8] text-xs font-bold uppercase tracking-widest mb-3">
      <Icon className="h-4 w-4" /> {label}
    </div>
    <div className="font-display text-3xl font-extrabold text-[#0F172A]">{value}</div>
  </div>
);

export default function Dashboard() {
  const { t } = useI18n();
  const { user, ready, becomeSeller } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [products, setProducts] = useState([]);
  const [upgrading, setUpgrading] = useState(false);
  const [coupons, setCoupons] = useState([]);
  const [couponForm, setCouponForm] = useState({ code: "", type: "percent", value: "", product_id: "all", max_uses: "" });
  const [creatingCoupon, setCreatingCoupon] = useState(false);

  const isSeller = user && (user.role === "seller" || user.role === "admin");

  useEffect(() => {
    if (ready && user === false) navigate("/login");
  }, [ready, user, navigate]);

  const load = () => {
    api.get("/dashboard/seller").then((r) => setData(r.data)).catch(() => {});
    api.get("/products/mine").then((r) => setProducts(r.data)).catch(() => {});
    api.get("/coupons/mine").then((r) => setCoupons(r.data)).catch(() => {});
  };

  useEffect(() => { if (isSeller) load(); }, [isSeller]);

  const createCoupon = async (e) => {
    e.preventDefault();
    setCreatingCoupon(true);
    try {
      await api.post("/coupons", {
        code: couponForm.code, type: couponForm.type, value: parseFloat(couponForm.value),
        product_id: couponForm.product_id === "all" ? null : couponForm.product_id,
        max_uses: couponForm.max_uses ? parseInt(couponForm.max_uses) : null,
      });
      toast.success("Cupón creado");
      setCouponForm({ code: "", type: "percent", value: "", product_id: "all", max_uses: "" });
      api.get("/coupons/mine").then((r) => setCoupons(r.data)).catch(() => {});
    } catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
    finally { setCreatingCoupon(false); }
  };

  const deleteCoupon = async (id) => {
    try {
      await api.delete(`/coupons/${id}`);
      setCoupons((c) => c.filter((x) => x.id !== id));
    } catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
  };

  const upgrade = async () => {
    setUpgrading(true);
    try { await becomeSeller(); toast.success("¡Ya eres vendedor!"); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    finally { setUpgrading(false); }
  };

  const del = async (id) => {
    try {
      await api.delete(`/products/${id}`);
      setProducts((p) => p.filter((x) => x.id !== id));
      toast.success("Producto eliminado");
      load();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  if (!ready || user === null) return <div className="py-32 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-[#0EA5E9]" /></div>;

  if (user && !isSeller) {
    return (
      <div className="max-w-lg mx-auto px-5 py-24 text-center">
        <div className="h-16 w-16 rounded-2xl bg-sky-50 text-[#0EA5E9] flex items-center justify-center mx-auto mb-6"><Store className="h-8 w-8" /></div>
        <h1 className="font-display text-3xl font-extrabold text-[#0F172A]">{t("nav.becomeSeller")}</h1>
        <p className="text-[#475569] mt-3">{t("home.ctaSellText")}</p>
        <Button onClick={upgrade} disabled={upgrading} data-testid="upgrade-seller-btn"
          className="mt-7 rounded-full h-12 px-8 bg-[#0EA5E9] hover:bg-[#0284C7] text-white font-bold">
          {upgrading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} {t("home.ctaSellBtn")}
        </Button>
      </div>
    );
  }

  const m = data?.metrics || {};
  return (
    <div className="max-w-7xl mx-auto px-5 py-10">
      <div className="flex items-center justify-between mb-8">
        <h1 className="font-display text-3xl md:text-4xl font-extrabold text-[#0F172A]">{t("dash.title")}</h1>
        <Button onClick={() => navigate("/dashboard/new")} data-testid="new-product-btn"
          className="rounded-full bg-[#0F172A] hover:bg-[#0EA5E9] text-white font-bold">
          <Plus className="h-4 w-4 mr-1" /> {t("dash.newProduct")}
        </Button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 border border-[#E2E8F0] rounded-2xl overflow-hidden divide-x divide-y lg:divide-y-0 divide-[#E2E8F0]" data-testid="dash-metrics">
        <Metric icon={Package} label={t("dash.products")} value={m.total_products ?? 0} />
        <Metric icon={TrendingUp} label={t("dash.sales")} value={m.total_sales ?? 0} />
        <Metric icon={DollarSign} label={t("dash.revenue")} value={`$${(m.revenue ?? 0).toFixed(2)}`} />
        <Metric icon={Eye} label={t("dash.views")} value={m.total_views ?? 0} />
      </div>

      <div className="grid lg:grid-cols-3 gap-8 mt-10">
        <div className="lg:col-span-2">
          <h2 className="font-display text-xl font-extrabold text-[#0F172A] mb-4">{t("dash.myProducts")}</h2>
          <p className="text-xs text-[#94A3B8] -mt-2 mb-4 flex items-center gap-1.5"><Clock className="h-3.5 w-3.5" /> {t("dash.pendingNote")}</p>
          <div className="border border-[#E2E8F0] rounded-2xl divide-y divide-[#E2E8F0] bg-white">
            {products.length === 0 && <div className="p-8 text-center text-[#94A3B8]">—</div>}
            {products.map((p) => (
              <div key={p.id} className="flex items-center gap-4 p-4" data-testid={`dash-product-${p.id}`}>
                <img src={fileUrl(p.preview_image_url)} alt="" className="h-14 w-14 rounded-xl object-cover bg-slate-100" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-[#0F172A] truncate">{p.title}</span>
                    <span className={`text-[10px] font-bold uppercase tracking-widest rounded-full px-2 py-0.5 shrink-0 ${p.status === "published" ? "bg-emerald-50 text-emerald-600" : p.status === "pending" ? "bg-amber-50 text-amber-600" : "bg-red-50 text-red-600"}`} data-testid={`status-${p.id}`}>
                      {p.status === "published" ? t("dash.statusPublished") : p.status === "pending" ? t("dash.statusPending") : t("dash.statusHiddenLabel")}
                    </span>
                  </div>
                  <div className="text-sm text-[#94A3B8]">${Number(p.price).toFixed(2)} · {p.sales_count || 0} {t("product.sales")}</div>
                </div>
                <Button size="sm" variant="outline" className="rounded-full" onClick={() => navigate(`/dashboard/edit/${p.id}`)} data-testid={`edit-${p.id}`}>
                  <Pencil className="h-3.5 w-3.5" />
                </Button>
                <Button size="sm" variant="outline" className="rounded-full text-red-600 hover:bg-red-50" onClick={() => del(p.id)} data-testid={`delete-${p.id}`}>
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            ))}
          </div>
        </div>

        <div>
          <h2 className="font-display text-xl font-extrabold text-[#0F172A] mb-4">{t("dash.recentOrders")}</h2>
          <div className="border border-[#E2E8F0] rounded-2xl bg-white p-2">
            {(data?.recent_orders || []).length === 0 && <div className="p-6 text-center text-[#94A3B8] text-sm">{t("dash.noOrders")}</div>}
            {(data?.recent_orders || []).map((o) => (
              <div key={o.id} className="flex items-center justify-between p-3 border-b border-[#E2E8F0] last:border-0">
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-[#0F172A] truncate">{o.product_title}</div>
                  <div className="text-xs text-[#94A3B8]">{o.buyer_name}</div>
                </div>
                <span className="text-sm font-bold text-[#0EA5E9]">${Number(o.amount).toFixed(2)}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Coupons */}
      <div className="mt-12">
        <div className="flex items-center gap-2 mb-4">
          <Ticket className="h-5 w-5 text-[#0EA5E9]" />
          <h2 className="font-display text-xl font-extrabold text-[#0F172A]">{t("dash.coupons")}</h2>
        </div>
        <div className="grid lg:grid-cols-3 gap-8">
          <form onSubmit={createCoupon} className="bg-white border border-[#E2E8F0] rounded-2xl p-6 space-y-4" data-testid="coupon-form">
            <Input value={couponForm.code} onChange={(e) => setCouponForm({ ...couponForm, code: e.target.value.toUpperCase() })}
              placeholder={t("dash.couponCode")} required data-testid="coupon-code" className="rounded-xl" />
            <div className="grid grid-cols-2 gap-3">
              <Select value={couponForm.type} onValueChange={(v) => setCouponForm({ ...couponForm, type: v })}>
                <SelectTrigger className="rounded-xl" data-testid="coupon-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="percent">{t("dash.couponPercent")}</SelectItem>
                  <SelectItem value="fixed">{t("dash.couponFixed")}</SelectItem>
                </SelectContent>
              </Select>
              <Input type="number" step="0.01" min="0" value={couponForm.value}
                onChange={(e) => setCouponForm({ ...couponForm, value: e.target.value })}
                placeholder={t("dash.couponValue")} required data-testid="coupon-value" className="rounded-xl" />
            </div>
            <Select value={couponForm.product_id} onValueChange={(v) => setCouponForm({ ...couponForm, product_id: v })}>
              <SelectTrigger className="rounded-xl" data-testid="coupon-product"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t("dash.couponAllProducts")}</SelectItem>
                {products.map((p) => <SelectItem key={p.id} value={p.id}>{p.title}</SelectItem>)}
              </SelectContent>
            </Select>
            <Input type="number" min="1" value={couponForm.max_uses}
              onChange={(e) => setCouponForm({ ...couponForm, max_uses: e.target.value })}
              placeholder={t("dash.couponMaxUses")} data-testid="coupon-maxuses" className="rounded-xl" />
            <Button type="submit" disabled={creatingCoupon} data-testid="coupon-create"
              className="w-full rounded-full bg-[#0F172A] hover:bg-[#0EA5E9] text-white font-bold">
              {creatingCoupon ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Plus className="h-4 w-4 mr-1" />}
              {t("dash.couponCreate")}
            </Button>
          </form>

          <div className="lg:col-span-2 border border-[#E2E8F0] rounded-2xl bg-white divide-y divide-[#E2E8F0]">
            {coupons.length === 0 && <div className="p-8 text-center text-[#94A3B8]">{t("dash.noCoupons")}</div>}
            {coupons.map((c) => (
              <div key={c.id} className="flex items-center gap-4 p-4" data-testid={`coupon-${c.id}`}>
                <div className="h-11 w-11 rounded-xl bg-sky-50 text-[#0EA5E9] flex items-center justify-center shrink-0"><Ticket className="h-5 w-5" /></div>
                <div className="flex-1 min-w-0">
                  <div className="font-display font-bold text-[#0F172A]">{c.code}
                    <span className="ml-2 text-sm font-normal text-[#0EA5E9]">
                      {c.type === "percent" ? `-${c.value}%` : `-$${c.value}`}
                    </span>
                  </div>
                  <div className="text-xs text-[#94A3B8]">
                    {c.product_title || t("dash.couponAllProducts")} · {c.uses || 0}{c.max_uses ? `/${c.max_uses}` : ""} {t("dash.couponUses")}
                  </div>
                </div>
                <Button size="sm" variant="outline" className="rounded-full text-red-600 hover:bg-red-50"
                  onClick={() => deleteCoupon(c.id)} data-testid={`coupon-delete-${c.id}`}>
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
